'use client';

import Link from 'next/link';
import JsonLd from '@/components/JsonLd';
import IndustryCrossLinks from "@/components/landing/IndustryCrossLinks";
import ResearchBacking from "@/components/landing/ResearchBacking";
import Footer from "@/components/shared/Footer";
import PublicHeader from "@/components/shared/PublicHeader";

const legalResearch = [
  {
    text: "Law firms spend an average of 240 hours per year on administrative tasks that could be automated.",
    source: "Thomson Reuters 2025 Report",
  },
  {
    text: "46% of law firms report losing qualified leads due to slow intake response times — most prospects contact multiple firms simultaneously.",
    source: "Clio Legal Trends Report 2024",
    url: "https://www.clio.com/resources/legal-trends/",
  },
  {
    text: "The average cost of client intake for law firms is $120–$250 per qualified lead when handled by paralegals or intake specialists.",
    source: "Law Society Practice Management Report",
    url: "https://www.lawsociety.org.uk/",
  },
];

const lawFirmFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is an AI intake specialist for law firms?",
      acceptedAnswer: { "@type": "Answer", text: "An AI intake specialist for law firms is a chatbot trained on your firm's practice areas, consultation procedures, and qualifying criteria. It responds to prospective client enquiries instantly, 24/7, collecting case details and screening for fit before they reach your calendar." },
    },
    {
      "@type": "Question",
      name: "How does AI legal intake automation work?",
      acceptedAnswer: { "@type": "Answer", text: "The AI uses retrieval-augmented generation (RAG) to answer questions from your uploaded knowledge base — practice areas, fee structures, and intake procedures. When a prospect qualifies, the AI collects their details and sends a structured intake request to your team. It only shares information you have explicitly provided." },
    },
    {
      "@type": "Question",
      name: "What are the limitations of AI agents for law firms?",
      acceptedAnswer: { "@type": "Answer", text: "AI agents cannot provide legal advice, represent clients, or create solicitor-client relationships. They are designed for structured tasks like intake screening, scheduling consultations, and answering FAQs about your firm. Complex legal questions are always escalated to your team." },
    },
  ],
};

const lawFirmSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Aditya Labs AI Intake Specialist for Law Firms",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "AI-powered intake specialist that vets cases, qualifies prospective clients, and collects retainer fees before they reach your calendar.",
  url: "https://adityalabs.ai/law-firms",
  audience: { "@type": "BusinessAudience", audienceType: "Law Firms and Solicitors" },
  provider: { "@type": "Organization", name: "Aditya Labs", url: "https://adityalabs.ai" },
  offers: { "@type": "Offer", price: "49", priceCurrency: "USD", description: "Plans from $49/month" },
};

export default function LawFirmLandingPage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans overflow-x-hidden selection:bg-amber-500/30">
      <JsonLd data={lawFirmSchema} />
      <JsonLd data={lawFirmFaqSchema} />

      {/* NAVBAR */}
      <PublicHeader variant="industry" accentColor="amber" maxWidth="max-w-7xl" />

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-24 lg:pt-32 lg:pb-40 overflow-hidden">

        {/* Ambient Background Glow (Gold/Navy for Prestige) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-600/10 rounded-full blur-[120px] -z-10 opacity-40"></div>

        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">

          {/* LEFT COLUMN: COPY */}
          <div className="space-y-10 relative z-10">

            {/* Urgency Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-bold tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              Average Billable Hour: $350+
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-7xl font-extrabold leading-tight tracking-tight">
              Stop Wasting <br />
              Solicitor Hours on <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-500">Cases That Don&apos;t Qualify.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg lg:text-xl text-gray-400 max-w-lg leading-relaxed">
              Your time is for paying clients. Our AI &ldquo;Intake Specialist&rdquo; vets cases, checks for conflicts, and collects retainer fees <strong className="text-white">before</strong> they get on your calendar.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/signup" className="flex items-center justify-center bg-amber-600 hover:bg-amber-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-amber-900/40 transition-all transform hover:-translate-y-1">
                Automate Intake
              </Link>
              <Link href="/demo#law-firm" className="flex items-center justify-center bg-white/5 hover:bg-white/10 border border-white/10 text-white px-8 py-4 rounded-xl font-medium backdrop-blur-sm transition-all">
                See Screening Logic
              </Link>
            </div>

            {/* Trust/Guarantee */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-sm text-gray-500 dark:text-gray-400 pt-6 border-t border-gray-800/50">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">Legal Professional Privilege Protected</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                <span className="font-medium text-gray-300">&ldquo;Billable Hour&rdquo; Guarantee</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 max-w-md">Save 10+ billable hours on intake screening in your first month, or your subscription is free. Works with Clio, Leap &amp; Osprey.</p>
            </div>
          </div>

          {/* RIGHT COLUMN: VISUAL PROOF (Intake Automation) */}
          <div className="relative mx-auto lg:mx-0 w-full max-w-md">

            {/* Glass Container */}
            <div className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 p-3 rounded-[2.5rem] border border-gray-700/50 shadow-2xl backdrop-blur-xl relative z-10">
              <div className="bg-black rounded-[2rem] overflow-hidden border border-gray-800 h-[650px] flex flex-col relative">

                {/* Status Bar */}
                <div className="bg-navy/90 backdrop-blur-md p-4 flex items-center justify-between border-b border-gray-800 sticky top-0 z-20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 border border-slate-600 flex items-center justify-center text-xs font-bold shadow-lg">AI</div>
                    <div>
                      <div className="font-semibold text-white text-sm">Legal Intake Bot</div>
                      <div className="text-[10px] text-amber-400 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                        Vetting New Lead
                      </div>
                    </div>
                  </div>
                </div>

                {/* Chat Area */}
                <div className="flex-1 p-5 space-y-6 overflow-y-auto bg-gray-950 scrollbar-hide">
                  <div className="text-center text-[10px] font-medium text-gray-600 dark:text-gray-400 uppercase tracking-widest my-4">New Web Inquiry</div>

                  {/* Message 1 (Lead) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      I need a lawyer. My business partner is suing me. Can I talk to someone?
                    </div>
                  </div>

                  {/* Message 2 (AI - Vetting) */}
                  <div className="flex justify-start">
                    <div className="bg-slate-800 text-white border border-slate-700 px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg">
                      I can help with that. To ensure we are the right fit, are the damages estimated to be over $50,000? ⚖️
                    </div>
                  </div>

                  {/* Message 3 (Lead) */}
                  <div className="flex justify-end">
                    <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl rounded-tr-sm max-w-[85%] text-sm leading-relaxed shadow-md">
                      Yes, it&apos;s closer to $200k.
                    </div>
                  </div>

                  {/* Message 4 (AI - Qualification) */}
                  <div className="flex justify-start">
                    <div className="bg-slate-800 text-white border border-slate-700 px-4 py-3 rounded-2xl rounded-tl-sm max-w-[85%] text-sm leading-relaxed shadow-lg">
                      Understood. Our consultation fee is $350. Would you like to secure your slot now?
                    </div>
                  </div>

                  {/* Payment Card Visual */}
                  <div className="flex justify-center my-4">
                    <div className="bg-gray-900 border border-gray-700 p-4 rounded-xl w-64 shadow-2xl">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-xs text-gray-400">Consultation Fee</span>
                        <span className="text-sm font-bold text-white">$350.00</span>
                      </div>
                      <div className="w-full bg-amber-600 text-white text-xs font-bold py-2 rounded text-center hover:bg-amber-500 transition">
                        Pay &amp; Book Now
                      </div>
                    </div>
                  </div>

                  {/* Success Notification */}
                  <div className="flex justify-center">
                    <div className="text-amber-400 text-xs flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      Payment Collected
                    </div>
                  </div>
                </div>

                {/* Input Area (Visual) */}
                <div className="p-4 bg-gray-900 border-t border-gray-800">
                  <div className="h-12 bg-gray-800 rounded-full w-full opacity-50 border border-gray-700"></div>
                </div>
              </div>
            </div>

            {/* Back Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-amber-600/10 blur-[90px] -z-10 rounded-full"></div>
          </div>
        </div>
      </section>
      {/* Social Proof */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">What Law Firms Are Saying</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center gap-1 mb-3">
                <span className="text-yellow-400 text-sm">★★★★★</span>
              </div>
              <p className="text-sm text-gray-300 italic leading-relaxed mb-4">
                &quot;The AI screened 120 inquiries in our first month and filtered out 70% that didn&apos;t meet our case criteria. My paralegals are now spending time on qualified leads instead of tire-kickers.&quot;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center text-white text-xs font-bold">JH</div>
                <div>
                  <p className="text-sm font-semibold text-white">James H.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Managing Partner, Personal Injury Firm, Manchester</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <div className="flex items-center gap-1 mb-3">
                <span className="text-yellow-400 text-sm">★★★★★</span>
              </div>
              <p className="text-sm text-gray-300 italic leading-relaxed mb-4">
                &quot;As a compliance-focused firm, data privacy was non-negotiable. The DLP engine and the clear AI disclosure on every interaction gave our compliance team the confidence to approve it.&quot;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center text-white text-xs font-bold">RM</div>
                <div>
                  <p className="text-sm font-semibold text-white">Rebecca M.</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Senior Partner, Corporate Law, London</p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center text-xs text-gray-600 mt-6">No solicitor-client relationship is created through AI interactions. All conversations clearly identify as AI-generated per SRA regulatory guidelines.</p>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-10 px-6">
        <div className="max-w-2xl mx-auto text-center bg-amber-900/20 border border-amber-700/30 rounded-2xl p-8">
          <p className="text-white font-semibold text-lg mb-2">Plans start at $49/month</p>
          <p className="text-gray-400 text-sm mb-4">One qualified lead covers your entire annual cost.</p>
          <Link href="/pricing" className="text-amber-400 hover:text-amber-300 text-sm font-medium underline">View all pricing plans →</Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[...lawFirmFaqSchema.mainEntity,
              { name: "How does the AI handle conflicts of interest?", acceptedAnswer: { text: "The AI does not perform conflict checks itself — that remains your firm's responsibility. However, the AI collects structured intake data (party names, opposing parties, case type) upfront, which your team can run through your existing conflict-check system before scheduling a consultation. This saves time by gathering the information needed for conflict screening before solicitor involvement." } },
              { name: "How long is intake conversation data stored?", acceptedAnswer: { text: "Conversation data is retained for 90 days by default. You can configure a custom retention period (30, 60, 90, or 365 days) from your dashboard settings. GDPR data subject deletion requests are fulfilled within 72 hours. All data is encrypted at rest (AES-256) and in transit (TLS 1.3). Conversation logs are stored in your account only — they are not used to train AI models." } },
            ].map((faq: { name: string; acceptedAnswer: { text: string } }, i: number) => (
              <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-3">{faq.name}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ResearchBacking stats={legalResearch} />
      <IndustryCrossLinks current="law-firms" />
      <Footer />
    </div>
  );
}
